// src/components/explorers/wildfireExplorer/tabs/DetailsTab.tsx
import React from 'react';
import { WildfireEntity } from '../../../../types/wildfire';
import { decodeResponse } from '../../../../easymap/liveLayers/liveFiresHelpers';
import './DetailsTab.scss';

interface DetailsTabProps {
  wildfire: WildfireEntity;
}

// Format hectares with comma separators and exactly one decimal (#565).
function formatHa(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return `${value.toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} ha`;
}

// "BEING_HELD" → "Being Held"
function formatStatus(status: string | null | undefined): string {
  if (!status) return '—';
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// Days since estimated start date.
function daysActive(startDate: string | null | undefined): number | null {
  if (!startDate) return null;
  const start = new Date(startDate).getTime();
  if (Number.isNaN(start)) return null;
  return Math.max(0, Math.floor((Date.now() - start) / 86_400_000));
}

export const DetailsTab: React.FC<DetailsTabProps> = ({ wildfire }) => {
  const data = wildfire.data || wildfire;

  // #565 — Fire Profile block sourced from the wildfire entity directly so
  // it renders on the initial Details view without an extra API call.
  // Mirrors the layout of the Fire Profile card on StatsTab.
  const fp = {
    status: (wildfire as any).wildfire_status,
    size: (wildfire as any).estimated_size,
    daysActive: daysActive((wildfire as any).estimated_start_date),
    response: data.responseCategoryId ? decodeResponse(data.responseCategoryId) : null,
    cause: (wildfire as any).cause,
    subCause: (wildfire as any).sub_cause,
    fireLevel: data.fireLevel ?? (wildfire as any).fire_level,
    initialAttackSuccess: data.initialAttackSuccess,
  };

  // Format coordinates with cardinal directions
  const formatLatitude = (lat: string | undefined) => {
    if (!lat) return 'N/A';
    return `${lat.replace('-', '')} N`;
  };

  const formatLongitude = (lon: string | undefined) => {
    if (!lon) return 'N/A';
    return `${lon.replace('-', '')} W`;
  };

  // Format datetime for both local and UTC display
  const formatDateTime = (dateTimeString: string | undefined): { localTime: string; utcTime: string } => {
    if (!dateTimeString) {
      return { localTime: 'N/A', utcTime: 'N/A' };
    }

    try {
      const date = new Date(dateTimeString);

      // Local time formatting
      const localTime = date.toLocaleString('en-CA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short'
      });

      // UTC time formatting
      const utcTime = date.toISOString().replace('T', ' ').replace('Z', ' UTC');

      return { localTime, utcTime };
    } catch (error) {
      console.warn('Invalid date format:', dateTimeString);
      return { localTime: 'Invalid Date', utcTime: 'Invalid Date' };
    }
  };

  return (
    <div className="wildfire-detail-content">
      <section className="detail-section">
        <h3>Fire Profile</h3>
        <div className="detail-grid">
          <div className="detail-item">
            <label>Status:</label>
            <span>{formatStatus(fp.status)}</span>
          </div>
          <div className="detail-item">
            <label>Size:</label>
            <span>{formatHa(fp.size)}</span>
          </div>
          <div className="detail-item">
            <label>Days Active:</label>
            <span>{fp.daysActive ?? '—'}</span>
          </div>
          <div className="detail-item">
            <label>Response:</label>
            <span>{fp.response || '—'}</span>
          </div>
          <div className="detail-item">
            <label>Cause:</label>
            <span>
              {fp.cause || '—'}
              {fp.subCause ? ` / ${fp.subCause}` : ''}
            </span>
          </div>
          <div className="detail-item">
            <label>Fire Level:</label>
            <span>{fp.fireLevel ?? '—'}</span>
          </div>
          {fp.initialAttackSuccess !== undefined && fp.initialAttackSuccess !== null && (
            <div className="detail-item">
              <label>Initial Attack:</label>
              <span>{fp.initialAttackSuccess ? 'Success' : 'Escaped'}</span>
            </div>
          )}
        </div>
      </section>

      <section className="detail-section">
        <h3>Geographic Information</h3>
        <div className="detail-grid">
          <div className="detail-item">
            <label>Latitude (DDM):</label>
            <span>{formatLatitude(data.latitude)}</span>
          </div>
          <div className="detail-item">
            <label>Longitude (DDM):</label>
            <span>{formatLongitude(data.longitude)}</span>
          </div>
          {data.latitudeDD && (
            <div className="detail-item">
              <label>Latitude (DD):</label>
              <span>{data.latitudeDD.toFixed(6)}°</span>
            </div>
          )}
          {data.longitudeDD && (
            <div className="detail-item">
              <label>Longitude (DD):</label>
              <span>{data.longitudeDD.toFixed(6)}°</span>
            </div>
          )}
        </div>
      </section>

      <section className="detail-section">
        <h3>Discovery Information</h3>
        <div className="detail-grid">
          <div className="detail-item">
            <label>Discovery Agent:</label>
            <span>{data.discoveryAgent || 'N/A'}</span>
          </div>
          <div className="detail-item">
            <label>Discovery Date:</label>
            <span>{data.discoveryDate || 'N/A'}</span>
          </div>
          <div className="detail-item">
            <label>Discovery Time:</label>
            <span>{data.discoveryTime || 'N/A'}</span>
          </div>
          <div className="detail-item">
            <label>Discovery Size:</label>
            <span>{data.discoverySize ? `${data.discoverySize} Ha` : 'N/A'}</span>
          </div>
        </div>
      </section>

      <section className="detail-section">
        <h3>Fire Classification</h3>
        <div className="detail-grid">
          <div className="detail-item">
            <label>Fire Level:</label>
            <span>{data.fireLevel || 'N/A'}</span>
          </div>
          <div className="detail-item">
            <label>Response Category:</label>
            <span>{(data.responseCategoryId && decodeResponse(data.responseCategoryId)) || 'N/A'}</span>
          </div>
          <div className="detail-item">
            <label>Initial Attack Success:</label>
            <span>{data.initialAttackSuccess !== undefined ? (data.initialAttackSuccess ? 'Yes' : 'No') : 'N/A'}</span>
          </div>
        </div>
      </section>

      <section className="detail-section">
        <h3>Timeline & Status</h3>
        <div className="detail-grid">
          <div className="detail-item">
            <label>Estimated Start Date:</label>
            <span>{data.estimatedStartDate || 'N/A'}</span>
          </div>
          <div className="detail-item">
            <label>Estimated Start Time:</label>
            <span>{data.estimatedStartTime || 'N/A'}</span>
          </div>
          <div className="detail-item">
            <label>Last Monitored:</label>
            <span>{data.lastMonitoredDate || 'N/A'}</span>
          </div>
          <div className="detail-item">
            <label>Action Date:</label>
            <span>{data.actionDate || 'N/A'}</span>
          </div>
          <div className="detail-item">
            <label>Action Size:</label>
            <span>{data.actionSize ? `${data.actionSize} Ha` : 'N/A'}</span>
          </div>
          <div className="detail-item">
            <label>Controlled Date:</label>
            <span>{data.controlledDate || 'N/A'}</span>
          </div>
          <div className="detail-item">
            <label>Controlled Size:</label>
            <span>{data.controlledSize ? `${data.controlledSize} Ha` : 'N/A'}</span>
          </div>
          {wildfire.updated_at && (
            <>
              <div className="detail-item">
                <label>DB Record Last Updated (Local):</label>
                <span>{formatDateTime(wildfire.updated_at).localTime}</span>
              </div>
              <div className="detail-item">
                <label>DB Record created from EMBER (Local):</label>
                <span>{formatDateTime(wildfire.created_at).localTime}</span>
              </div>
            </>
          )}
        </div>
      </section>

      {data.actionComments && (
        <section className="detail-section">
          <h3>Action Comments</h3>
          <div className="detail-full-width">
            <p>{data.actionComments}</p>
          </div>
        </section>
      )}

      {data.postComment && (
        <section className="detail-section">
          <h3>Post Comments</h3>
          <div className="detail-full-width">
            <p>{data.postComment}</p>
          </div>
        </section>
      )}

      {data.monitoredHistory && data.monitoredHistory.length > 0 && (
        <section className="detail-section">
          <h3>Monitoring History</h3>
          <div className="detail-full-width">
            <ul>
              {data.monitoredHistory.map((entry: any, idx: number) => (
                <li key={idx}>
                  {entry.lastMonitoredDate || 'Unknown date'}
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {data.smokeReports && data.smokeReports.length > 0 && (
        <section className="detail-section">
          <h3>Smoke Reports</h3>
          <div className="detail-full-width">
            <p>{data.smokeReports.length} smoke report(s) on file</p>
          </div>
        </section>
      )}
    </div>
  );
};
